import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, ChevronLeft, ChevronRight, CalendarCheck, Receipt, Calculator, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import { PiktableLoader } from "@/components/PiktableLoader";
import { useState, useMemo } from "react";
import { useBillingData } from "@/hooks/useBillingData";
import { useEstablishment } from "@/contexts/EstablishmentContext";
import { calculateBilling, formatCHF, getPlanLabel } from "@/lib/billingPlans";

const Billing = () => {
  const { data: monthsData, loading, error, refetch } = useBillingData();
  const { currentEstablishment } = useEstablishment();
  const [selectedMonth, setSelectedMonth] = useState<string>("");

  // Set default month to the first available month
  const effectiveSelectedMonth = selectedMonth || monthsData[0]?.month || "";

  const selectedMonthData = useMemo(() => {
    return monthsData.find(m => m.month === effectiveSelectedMonth);
  }, [monthsData, effectiveSelectedMonth]);

  // Calculer l'estimation de facturation pour le mois sélectionné
  const billingEstimate = useMemo(() => {
    if (!selectedMonthData || !currentEstablishment) return null;
    const planType = currentEstablishment.planType || 'trial';
    return calculateBilling(planType, selectedMonthData.arrived);
  }, [selectedMonthData, currentEstablishment]);

  // Calculer les estimations de facturation pour tous les mois
  const monthsWithBilling = useMemo(() => {
    if (!currentEstablishment) return [];
    const planType = currentEstablishment.planType || 'trial';
    return monthsData.map(month => ({
      ...month,
      billing: calculateBilling(planType, month.arrived)
    }));
  }, [monthsData, currentEstablishment]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    const currentIndex = monthsData.findIndex(m => m.month === effectiveSelectedMonth);
    if (direction === 'prev' && currentIndex < monthsData.length - 1) {
      setSelectedMonth(monthsData[currentIndex + 1].month);
    } else if (direction === 'next' && currentIndex > 0) {
      setSelectedMonth(monthsData[currentIndex - 1].month);
    }
  };

  if (loading) {
    return <PiktableLoader message="Chargement de la facturation..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/dashboard" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au tableau de bord
          </Link>
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">{error}</p>
              <Button onClick={refetch} variant="outline" className="mt-4">
                Réessayer
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Link to="/dashboard" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au tableau de bord
            </Link>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
              <Receipt className="w-7 h-7 text-primary" />
              Facturation
            </h1>
            <p className="text-muted-foreground mt-1">
              Suivi des réservations venues pour la facturation mensuelle
            </p>
          </div>
          <Link to="/dashboard/statistics">
            <Button variant="outline" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Voir les statistiques
            </Button>
          </Link>
        </div>

        {/* Month Selector */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateMonth('prev')}
                disabled={monthsData.findIndex(m => m.month === effectiveSelectedMonth) >= monthsData.length - 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <Select value={effectiveSelectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Sélectionner un mois" />
                </SelectTrigger>
                <SelectContent>
                  {monthsData.map(month => (
                    <SelectItem key={month.month} value={month.month}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateMonth('next')}
                disabled={monthsData.findIndex(m => m.month === effectiveSelectedMonth) <= 0}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Réservations venues */}
        {selectedMonthData && (
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <CalendarCheck className="w-4 h-4" />
                Réservations venues (base de facturation)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-primary">
                {selectedMonthData.arrived}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                sur {selectedMonthData.total} réservations totales ce mois
              </p>
            </CardContent>
          </Card>
        )}

        {/* Estimation de facturation */}
        {billingEstimate && currentEstablishment && (
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-primary" />
                Estimation de facturation - {selectedMonthData?.label}
              </CardTitle>
              <CardDescription>
                Plan actuel : {getPlanLabel(currentEstablishment.planType || 'trial')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {billingEstimate.isFree ? (
                <div className="text-center py-4">
                  <p className="text-lg text-muted-foreground">
                    {billingEstimate.planName === 'Essai' ? 'Période d\'essai gratuite' : 'Plan gratuit'}
                  </p>
                  <p className="text-3xl font-bold text-primary mt-2">0.00 CHF</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span>Abonnement de base</span>
                    <span>{formatCHF(billingEstimate.baseAmount)}</span>
                  </div>
                  <div className="border-t border-border pt-2" />
                  <div className="flex justify-between text-sm">
                    <span>Commissions brutes ({billingEstimate.reservationCount} × tarif)</span>
                    <span className={billingEstimate.isCapped ? 'line-through text-muted-foreground' : ''}>
                      {formatCHF(billingEstimate.rawReservationAmount)}
                    </span>
                  </div>
                  {billingEstimate.maxCommissions > 0 && (
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Plafond commissions</span>
                      <span>{formatCHF(billingEstimate.maxCommissions)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span>Commissions facturées</span>
                    <span className={billingEstimate.isCapped ? 'text-amber-600 font-medium' : ''}>
                      {formatCHF(billingEstimate.reservationAmount)}
                      {billingEstimate.isCapped && ' ✓'}
                    </span>
                  </div>
                  <div className="border-t border-border pt-2" />
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total HT estimé</span>
                    <span className={`text-2xl font-bold ${billingEstimate.isCapped ? 'text-amber-600' : 'text-primary'}`}>
                      {formatCHF(billingEstimate.total)}
                      {billingEstimate.isCapped && ' (commissions plafonnées)'}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Monthly History Table - Simplified for billing */}
        <Card>
          <CardHeader>
            <CardTitle>Historique de facturation</CardTitle>
            <CardDescription>
              Récapitulatif des réservations venues et montants estimés
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mois</TableHead>
                  <TableHead className="text-center">Réservations venues</TableHead>
                  <TableHead className="text-right">Montant estimé HT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthsWithBilling.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                      Aucune donnée disponible
                    </TableCell>
                  </TableRow>
                ) : (
                  monthsWithBilling.map((month) => (
                    <TableRow 
                      key={month.month}
                      className={month.month === effectiveSelectedMonth ? "bg-muted/50" : ""}
                    >
                      <TableCell className="font-medium">{month.label}</TableCell>
                      <TableCell className="text-center font-semibold text-primary">
                        {month.arrived}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={month.billing.isCapped ? 'text-amber-600' : ''}>
                          {month.billing.isFree ? '0.00 CHF' : formatCHF(month.billing.total)}
                          {month.billing.isCapped && ' *'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {monthsWithBilling.some(m => m.billing.isCapped) && (
              <p className="text-xs text-muted-foreground mt-4">
                * Commissions plafonnées au maximum du plan
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Billing;
